import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import HeaderLogo from "../common/HeaderLogo";
import AppVersion from "../common/AppVersion";
import { resetDefaults as defaults } from "../resetPassword/ResetConstant";
import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import superagent from "superagent";
import { apiUrl } from "../common/apiUrl";
import PasswordExpiredModal from "./PasswordExpiredModal";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ResetPassword = ({ queryAuth, setQueryAuth }) => {
  const [userName, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cpassword, setCPassword] = useState("");
  const [isResetDisable, setResetDisable] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [maskedPassword, setMaskedPassword] = useState(true);
  const [maskedCPassword, setMaskedCPassword] = useState(true);

  const {
    returnToLogin,
    resetPassword,
    confirmPassword,
    newPassword,
    passwordMatchError,
    messageResetSuccess,
    messageTokenValid,
    messageInvalidKeys,
    messagePasswordReset,
    messagePasswordModal,
    messageWrongError,
    messageUnauthorizedReset,
    rules,
  } = defaults;

  useEffect(() => {
    superagent
      .get(
        `${apiUrl("/auth/key/validate")}?username=${encodeURIComponent(
          queryAuth.User
        )}&auth_key=${encodeURIComponent(queryAuth.Auth_Key)}`
      )
      .then((res) => {
        if (res?.body?.message === messageTokenValid)
          setUsername(queryAuth.User);
      })
      .catch(() => {
        setQueryAuth({ ...queryAuth, error: messageInvalidKeys });
      });
  }, []);

  useEffect(() => {
    if (password !== "" && cpassword !== "") {
      setResetDisable(false);
      return;
    }
    if (!isResetDisable) setResetDisable(true);
  }, [password, cpassword]);

  const handlePasswordChange = (e) => {
    if (e.target.name === "password") setPassword(e.target.value);
    else if (e.target.name === "cpassword") setCPassword(e.target.value);
  };

  const handleResetSubmit = (e) => {
    setErrorMessage("");
    e.preventDefault();

    if (password !== cpassword) {
      setErrorMessage(passwordMatchError);
      return;
    }
    superagent
      .post(apiUrl("/auth/password/reset"))
      .send({
        username: userName,
        authKey: queryAuth.Auth_Key,
        newPassword: password,
        confirmPassword: cpassword,
      })
      .then(() => {
        setResetSuccess(messageResetSuccess);
        setResetDisable(true);
        setTimeout(() => {
          window.location.href = process.env.REACT_APP_FAB;
        }, 5000);
      })
      .catch((err) => {
        const apiMessage =
          err?.response?.body?.message ||
          err?.response?.body?.error?.message ||
          err?.message ||
          "";
        if (
          String(apiMessage).includes("Password Reset not authorized") ||
          String(apiMessage).includes(messageUnauthorizedReset)
        ) {
          setQueryAuth({ ...queryAuth, error: messageInvalidKeys });
          return;
        }
        setErrorMessage(apiMessage || messageWrongError);
      });
  };

  const getMaskedProps = (setMasked, masked) => {
    return {
      input: {
        endAdornment: (
          <IconButton onClick={() => setMasked(!masked)} edge="end">
            {masked ? (
              <VisibilityOutlined className="visibility-icon" />
            ) : (
              <VisibilityOffOutlined className="visibility-icon" />
            )}
          </IconButton>
        ),
      },
    };
  };

  const allRulesPassed = (password, cpassword) => {
    return Object.entries(rules).every(([key, rule]) => {
      return key === "match"
        ? rule.isValid(password, cpassword)
        : rule.isValid(password);
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderLogo />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        data-testid="reset-password-box"
      >
        <Paper
          sx={{
            backgroundColor: "background.primary",
            border: "1px solid var(--elevation-outlined, #FFFFFF1F)",
            color: "text.primary",
            p: 4,
            width: 450,
          }}
        >
          <Typography
            variant="h4"
            lineHeight="42px"
            letterSpacing="0.5px"
            marginBottom="20px"
          >
            {resetPassword}
          </Typography>
          <form onSubmit={handleResetSubmit}>
            <Box sx={{ mb: 2 }}>
              <TextField
                disabled
                fullWidth
                margin="normal"
                label="Username"
                variant="outlined"
                value={userName}
                data-testid="username"
              />
              <TextField
                fullWidth
                margin="normal"
                label={newPassword}
                variant="outlined"
                type={maskedPassword ? "password" : "text"}
                name="password"
                value={password}
                data-testid="password"
                onChange={handlePasswordChange}
                slotProps={getMaskedProps(setMaskedPassword, maskedPassword)}
              />
              <TextField
                fullWidth
                margin="normal"
                label={confirmPassword}
                variant="outlined"
                type={maskedCPassword ? "password" : "text"}
                name="cpassword"
                value={cpassword}
                data-testid="re-password"
                onChange={handlePasswordChange}
                slotProps={getMaskedProps(setMaskedCPassword, maskedCPassword)}
              />
            </Box>
            <Box sx={{ pb: 3 }}>
              <Typography type={"subtitle2"} sx={{ fontWeight: "bold", pb: 2 }}>
                Password Requirements:
              </Typography>
              {Object.entries(rules).map(([key, rule]) => {
                let valid =
                  key === "match"
                    ? rule.isValid(password, cpassword)
                    : rule.isValid(password);
                return (
                  <FormControlLabel
                    data-testid="password-checkbox-validations"
                    key={key}
                    control={
                      <Checkbox
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        checked={valid && password.length > 0}
                        readOnly
                      />
                    }
                    label={rule.label}
                    sx={{ opacity: 0.8 }}
                  />
                );
              })}
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isResetDisable || !allRulesPassed(password, cpassword)}
              data-testid="reset-password-button"
            >
              {resetPassword}
            </Button>
          </form>
          <Box
            fullWidth
            textAlign="center"
            mt={errorMessage != "" || resetSuccess != "" ? 2 : 4}
          >
            {resetSuccess !== "" && (
              <Typography color="success">{resetSuccess}</Typography>
            )}
            {errorMessage !== "" && (
              <Typography color="error">{errorMessage}</Typography>
            )}
            <Button
              variant="text"
              color="primary"
              size="large"
              data-testid="return-login-button"
              onClick={() => (window.location.href = process.env.REACT_APP_FAB)}
            >
              {returnToLogin}
            </Button>
          </Box>
        </Paper>
      </Box>
      {queryAuth?.error === messageInvalidKeys && (
        <PasswordExpiredModal
          setQueryAuth={setQueryAuth}
          modalHeader={messagePasswordReset}
          modalContent={messagePasswordModal}
          username={queryAuth.User || userName}
        />
      )}
      <AppVersion />
    </Box>
  );
};

export default ResetPassword;
